import { useEffect, useRef } from "react";
import axios from "axios";
import { readStoredSettings } from "@/lib/userSettings";
import { showServiceWorkerNotification } from "@/lib/pushNotifications";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EVENT_NOTIFICATION_STORAGE_KEY = "lotq_browser_event_notifications";

function browserNotificationsEnabled() {
  if (typeof window === "undefined" || typeof Notification === "undefined") return false;
  const settings = readStoredSettings();
  return settings.browserNotifications === true && Notification.permission === "granted";
}

function readEventNotificationState() {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(EVENT_NOTIFICATION_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeEventNotificationState(state) {
  if (typeof window === "undefined") return;
  localStorage.setItem(EVENT_NOTIFICATION_STORAGE_KEY, JSON.stringify(state));
}

async function showBrowserNotification(title, options = {}, targetPath = "/") {
  if (!browserNotificationsEnabled()) return null;
  return showServiceWorkerNotification(title, options, targetPath);
}

function parseEventDateTime(ticket) {
  const date = ticket?.event?.date;
  const time = ticket?.event?.time;
  if (!date || !time) return null;

  const candidate = new Date(`${date}T${time}`);
  return Number.isNaN(candidate.getTime()) ? null : candidate;
}

function getReminderWindowKey(diffMs) {
  const hours = diffMs / (1000 * 60 * 60);
  if (hours > 0 && hours <= 3) return "3h";
  if (hours > 3 && hours <= 24) return "24h";
  return null;
}

function getReminderCopy(ticket, diffMs) {
  const hours = diffMs / (1000 * 60 * 60);
  const venue = ticket?.event?.venue?.name || ticket?.event?.location;
  const schedule = [ticket?.event?.date, ticket?.event?.time].filter(Boolean).join(" às ");

  if (hours <= 3) {
    return {
      title: "Seu evento está chegando",
      body: `${ticket?.event?.title} começa em breve${venue ? ` • ${venue}` : ""}${schedule ? ` • ${schedule}` : ""}`,
    };
  }

  return {
    title: "Lembrete de evento do Lord of the Quiz",
    body: `${ticket?.event?.title} acontece nas próximas 24h${venue ? ` • ${venue}` : ""}${schedule ? ` • ${schedule}` : ""}`,
  };
}

export function notifyAchievementBrowser(achievement) {
  if (!browserNotificationsEnabled()) return;
  if (typeof document !== "undefined" && document.visibilityState === "visible") return;

  const title = achievement?.title || "Nova conquista desbloqueada";
  const body = achievement?.description || "Você desbloqueou uma nova conquista no Lord of the Quiz.";

  void showBrowserNotification(title, { body, tag: `achievement-${achievement?.id || title}` }, "/perfil");
}

export default function useBrowserNotifications(user) {
  const lastCheckRef = useRef(0);

  useEffect(() => {
    if (!user) return undefined;
    if (!browserNotificationsEnabled()) return undefined;
    if (user.event_reminders === false) return undefined;

    let cancelled = false;

    const checkUpcomingEvents = async () => {
      const now = Date.now();
      if (now - lastCheckRef.current < 15 * 60 * 1000) return;
      lastCheckRef.current = now;

      const token = localStorage.getItem("auth_token");
      if (!token) return;

      try {
        const response = await axios.get(`${API}/bookings/my-tickets`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (cancelled) return;

        const alreadyNotified = readEventNotificationState();
        const nextState = { ...alreadyNotified };

        (response.data || []).forEach((ticket) => {
          const eventDateTime = parseEventDateTime(ticket);
          if (!eventDateTime) return;

          const diffMs = eventDateTime.getTime() - Date.now();
          const windowKey = getReminderWindowKey(diffMs);
          if (!windowKey) return;

          const storageKey = `${ticket.id}:${windowKey}`;
          if (nextState[storageKey]) return;

          const copy = getReminderCopy(ticket, diffMs);
          void showBrowserNotification(copy.title, {
            body: copy.body,
            tag: `event-reminder-${storageKey}`,
          }, "/meus-ingressos");
          nextState[storageKey] = new Date().toISOString();
        });

        writeEventNotificationState(nextState);
      } catch {
        // silent on purpose
      }
    };

    checkUpcomingEvents();
    const intervalId = window.setInterval(checkUpcomingEvents, 15 * 60 * 1000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [user]);
}
